import { FC, FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card";
import {
  useMediaAssets,
  useMediaAdmin,
  MediaType,
  MediaItem,
} from "lib/mediaQueries";
import "./MediaAdmin.scss";

const formatSize = (bytes: number): string => {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const MediaAdmin: FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useMediaAssets();
  const { upload, remove } = useMediaAdmin();

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<MediaType>("video");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const uploading = progress !== null;
  const canSubmit = Boolean(file) && title.trim().length > 0 && !uploading;

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!file) return;
    setError(null);
    setProgress(0);
    try {
      await upload(
        { file, title: title.trim(), description: description.trim(), type },
        setProgress
      );
      setFile(null);
      setTitle("");
      setDescription("");
      setType("video");
    } catch {
      setError(t("mediaAdmin.uploadError"));
    } finally {
      setProgress(null);
    }
  };

  const onDelete = async (item: MediaItem): Promise<void> => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("mediaAdmin.confirmDelete", { title: item.title })))
      return;
    setBusyId(item.id);
    try {
      await remove(item.id);
    } finally {
      setBusyId(null);
    }
  };

  const items = data ?? [];

  return (
    <div className="media-admin">
      <h1 className="media-admin__heading">{t("mediaAdmin.title")}</h1>
      <p className="media-admin__intro">{t("mediaAdmin.intro")}</p>

      <Card customClass="media-admin__form-card">
        <form onSubmit={onSubmit} className="media-admin__form">
          <label className="media-admin__field" htmlFor="media-type">
            <span>{t("mediaAdmin.type")}</span>
            <select
              id="media-type"
              value={type}
              onChange={(e) => setType(e.target.value as MediaType)}
              disabled={uploading}
            >
              <option value="video">{t("mediaAdmin.typeWebinar")}</option>
              <option value="doc">{t("mediaAdmin.typeDoc")}</option>
            </select>
          </label>

          <label className="media-admin__field" htmlFor="media-file">
            <span>{t("mediaAdmin.file")}</span>
            <input
              id="media-file"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={uploading}
              data-testid="media-file-input"
            />
          </label>

          <label className="media-admin__field" htmlFor="media-title">
            <span>{t("mediaAdmin.titleLabel")}</span>
            <input
              id="media-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={uploading}
              maxLength={200}
              data-testid="media-title-input"
            />
          </label>

          <label className="media-admin__field" htmlFor="media-description">
            <span>{t("mediaAdmin.description")}</span>
            <textarea
              id="media-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
              rows={2}
              maxLength={2000}
            />
          </label>

          {uploading && (
            <div className="media-admin__progress" aria-live="polite">
              <div className="media-admin__progress-bar">
                <div
                  className="media-admin__progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span>{t("mediaAdmin.uploading", { percent: progress })}</span>
            </div>
          )}

          {error && <div className="media-admin__error">{error}</div>}

          <button
            type="submit"
            className="media-admin__submit"
            disabled={!canSubmit}
          >
            {uploading ? t("mediaAdmin.uploading", { percent: progress }) : t("mediaAdmin.upload")}
          </button>
        </form>
      </Card>

      <h2 className="media-admin__section-label">{t("mediaAdmin.existing")}</h2>

      {isLoading && (
        <div className="media-admin__state">{t("common.loading")}</div>
      )}
      {isError && (
        <div className="media-admin__state">{t("mediaAdmin.loadError")}</div>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <div className="media-admin__state">{t("mediaAdmin.empty")}</div>
      )}

      {items.length > 0 && (
        <ul className="media-admin__list">
          {items.map((item) => (
            <li key={item.id} className="media-admin__row">
              <span className={`media-admin__badge media-admin__badge--${item.type}`}>
                {item.type === "video"
                  ? t("mediaAdmin.typeWebinar")
                  : t("mediaAdmin.typeDoc")}
              </span>
              <span className="media-admin__row-title">{item.title}</span>
              <span className="media-admin__row-file">{item.filename}</span>
              <span className="media-admin__row-size">
                {formatSize(item.sizeBytes)}
              </span>
              <button
                type="button"
                className="media-admin__delete"
                onClick={() => onDelete(item)}
                disabled={busyId === item.id}
              >
                {busyId === item.id
                  ? t("mediaAdmin.deleting")
                  : t("mediaAdmin.delete")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MediaAdmin;
